import { type CountryCode } from '@constants'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { type BrandInfo, type UserRoleType } from '../constants'

interface StmBrandStoreState {
  currentBrand: BrandInfo | null
  brands: BrandInfo[]
  expiredCampaignCodes: string[]
  /**
   * 這個 store 屬於哪個 country。登入時由 AuthProcessing 寫入。
   * 用來判斷「目前 URL country」與「store 內登入狀態的 country」是否一致。
   * 不一致時 StmRouters 會視為未登入並走 UnauthRoutes（防止 hk tab 開啟後讀到 tw 的殘留登入狀態）。
   */
  countryCode: CountryCode | null
  /**
   * userRole 用於整理多品牌帳號的角色權限，僅用於部分頁面顯示邏輯，實際權限控管與經銷商選擇等功能仍以 API 回傳為主。
   */
  userRole: UserRoleType | null
  setBrand: (campaignCode: string | null) => void
  setBrands: (brands: BrandInfo[]) => void
  setExpiredCampaignCodes: (campaignCode: string) => void
  setCountryCode: (countryCode: CountryCode | null) => void
  setUserRole: (userRole: UserRoleType | null) => void
}

export const useStmBrandStore = create<StmBrandStoreState>()(
  persist(
    set => ({
      currentBrand: null,
      brands: [],
      expiredCampaignCodes: [],
      countryCode: null,
      userRole: null,
      setBrand: campaignCode => set((state) => {
        if (!campaignCode) return { currentBrand: null }
        const brand = state.brands.find(brand => brand.campaignCode === campaignCode)
        return { currentBrand: brand ?? null }
      }),
      setBrands: brands => set({ brands, expiredCampaignCodes: [] }),
      setExpiredCampaignCodes: campaignCode => set((state) => {
        if (state.expiredCampaignCodes.includes(campaignCode)) return state
        return { expiredCampaignCodes: [...state.expiredCampaignCodes, campaignCode] }
      }),
      setCountryCode: countryCode => set({ countryCode }),
      setUserRole: userRole => set({ userRole }),
    }),
    {
      name: 'stmBrandStorage',
      version: 4,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState, version) => {
        const state = persistedState as StmBrandStoreState
        if (version === 0) {
          return {
            ...state,
            userRole: null,
            countryCode: state.brands?.[0]?.countryCode ?? null,
            expiredCampaignCodes: [],
          }
        }
        if (version === 1) {
          return {
            ...state,
            countryCode: state.brands?.[0]?.countryCode ?? null,
            expiredCampaignCodes: [],
          }
        }
        if (version === 2 || version === 3) {
          return {
            ...state,
            expiredCampaignCodes: [],
          }
        }
        return state
      },
    },
  ),
)
